import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { CircleNotch, CheckCircle, XCircle } from "@phosphor-icons/react";
import { BackButton } from "@/components/BackButton";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useSubscription();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        // keep trying
      }
      if (attempts.current >= 10) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refresh]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-secondary/40 px-4" data-testid="payment-success-page">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
        <div className="-mt-4 mb-2 text-left"><BackButton fallback="/o-meu-plano" className="-ml-4" testid="payment-success-back" /></div>
        {state === "checking" && (
          <>
            <CircleNotch size={40} className="mx-auto animate-spin text-primary" />
            <h1 className="mt-4 font-display text-xl font-extrabold">A confirmar o pagamento…</h1>
            <p className="mt-1 text-sm text-muted-foreground">Aguarde um instante, não feche esta página.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle size={52} weight="fill" className="mx-auto text-emerald-500" />
            <h1 className="mt-4 font-display text-2xl font-extrabold">Subscrição ativada!</h1>
            <p className="mt-1 text-sm text-muted-foreground">Obrigado. A sua empresa já tem acesso completo ao GESTA CV.</p>
            <Button className="mt-6 rounded-full" onClick={() => navigate("/")} data-testid="go-dashboard-btn">
              Ir para o painel
            </Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <CircleNotch size={40} className="mx-auto text-amber-500" />
            <h1 className="mt-4 font-display text-xl font-extrabold">Pagamento em processamento</h1>
            <p className="mt-1 text-sm text-muted-foreground">Pode demorar alguns instantes. Atualize a página de subscrição dentro de momentos.</p>
            <Button className="mt-6 rounded-full" onClick={() => navigate("/o-meu-plano")}>Ver subscrição</Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={52} weight="fill" className="mx-auto text-red-500" />
            <h1 className="mt-4 font-display text-xl font-extrabold">Não foi possível confirmar</h1>
            <p className="mt-1 text-sm text-muted-foreground">O pagamento não foi concluído ou foi cancelado.</p>
            <Button className="mt-6 rounded-full" variant="outline" onClick={() => navigate("/o-meu-plano")}>Tentar novamente</Button>
          </>
        )}
      </div>
    </div>
  );
}
